// lib/apiClient.ts
import axios, { AxiosError } from "axios";
import { getToken, clearAuthData } from "./auth";
import { API_BASE_URL, ROUTES } from "./constants";
import { getErrorMessage } from "./utils";
import { makeCacheKey, getCache, setCache } from "./dashboardCache";
import {
  ApiErrorResponse,
  ApiResponse,
  AuthOtpRequestPayload,
  AuthOtpVerifyPayload,
  AuthOtpVerifyResponse,
  UserKycPayload,
  UserTelegramPayload,
  UserProfile,
  UserSubscription,
  Channel,
  Plan,
  InitiateTransactionOrderResponse,
  SubscriptionUpgradePayload,
  ChannelCreatePayload,
  ChannelUpdatePayload,
  ChannelPlatform,
  PopulatedChannel,
  SubscriptionAdminResponse,
  SubscriptionExtendPayload,
  PlanCreatePayload,
  PlanUpdatePayload,
  ReminderTemplate,
  PasswordLoginPayload,
  PasswordLoginResponse,
  AdminRegistrationPayload,
  DashboardSummaryResponse,
  ListResponseWithTotal,
  DOBPayload,
  AdminDashboardSummaryResponse,
  ChannelDashboardSummaryResponse,
  ComboDashboardSummaryResponse,
  NameMailPayload,
  TransactionAdminResponse,
  Combo,
  PopulatedCombo,
  ComboCreatePayload,
  ComboUpdatePayload,
  Coupon,
  CouponPreviewResponse,
  CouponUpsertPayload,
  UniversalCoupon,
  UniversalCouponUpsertPayload,
  ClientConfigData,
  ClientConfigRecord,
  ClientConfigValidationItem,
  TelegramWebhookSyncInfo,
} from "../types";

// Create Axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 15000,
});

// Request interceptor to add token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for global error handling (like 401)
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    if (error.response?.status === 401) {
      console.error(
        "API Error: Unauthorized (401). Clearing auth data and redirecting."
      );
      clearAuthData();
      if (typeof window !== "undefined") {
        let loginRoute = ROUTES.LOGIN_USER; // Default
        if (window.location.pathname.startsWith("/admin")) {
          loginRoute = ROUTES.LOGIN_ADMIN;
        } else if (window.location.pathname.startsWith("/superadmin")) {
          loginRoute = ROUTES.LOGIN_ADMIN;
        }

        const authPaths = [
          ROUTES.LOGIN_USER,
          ROUTES.LOGIN_ADMIN,
          ROUTES.VERIFY_OTP,
        ];
        if (!authPaths.some((p) => window.location.pathname.startsWith(p))) {
          console.log(`Redirecting to ${loginRoute} due to 401`);
          window.location.href = loginRoute;
        } else {
          console.log(
            "Already on auth page or window undefined, skipping redirect."
          );
        }
      }
    }
    const errorMessage = getErrorMessage(error);
    return Promise.reject(new Error(errorMessage));
  }
);

// Helper to extract data or throw error message string
const handleApiResponse = <T>(response: {
  data: ApiResponse<T> | ApiErrorResponse;
  status: number;
}): T => {
  // Handle 204 explicitly (no content)
  if (response.status === 204) {
    return undefined as T;
  }
  if ('status' in response.data && response.data.status === "success") {
    if ("data" in response.data && response.data.data !== undefined) {
      return response.data.data as T;
    }
    return undefined as T;
  } else {
    throw new Error(
      (response.data as ApiErrorResponse).message ||
      "API request failed with non-success status"
    );
  }
};


// Helper to handle list responses with total count
// helper: now keyed return
const handleListResponseWithTotal = <T, K extends string>(
  response: { data: ListResponseWithTotal<{}> | ApiErrorResponse },
  key: K
): { [P in K]: T[] } & { total: number } => {
  if (response.data.status !== "success") {
    throw new Error((response.data as ApiErrorResponse).message);
  }
  const list = response.data as ListResponseWithTotal<{}>;
  const arr = list.data?.[key] as T[] | undefined;
  if (!Array.isArray(arr)) {
    throw new Error(`Expected data.${key} to be array`);
  }
  return { [key]: arr, total: list.total ?? arr.length } as any;
};

// --- Auth ---
export const requestOtp = async (
  payload: AuthOtpRequestPayload
): Promise<ApiResponse<{ devOtp?: string }>> => {
  const response = await axiosInstance.post<ApiResponse | ApiErrorResponse>(
    "/auth/otp/request",
    payload
  );
  if (response.data.status !== "success") {
    throw new Error(
      (response.data as ApiErrorResponse).message || "Failed to send OTP"
    );
  }
  return response.data as ApiResponse<{ devOtp?: string }>;
};

export const verifyOtp = async (
  payload: AuthOtpVerifyPayload
): Promise<AuthOtpVerifyResponse> => {
  const response = await axiosInstance.post<
    AuthOtpVerifyResponse | ApiErrorResponse
  >("/auth/otp/verify", payload);
  // Ensure all expected user fields from backend are checked
  // Using the specific AuthContextUserApiResponse type for checking
  const userData = (response.data as AuthOtpVerifyResponse).data?.user;
  if (
    response.data.status === "success" &&
    "token" in response.data &&
    userData?.id &&
    userData.role &&
    userData.isKycSubmitted !== undefined &&
    userData.telegramIdLinked !== undefined
  ) {
    return response.data as AuthOtpVerifyResponse;
  }
  throw new Error(
    (response.data as ApiErrorResponse).message ||
    "OTP Verification failed or returned invalid data."
  );
};

export const loginPassword = async (
  payload: PasswordLoginPayload
): Promise<PasswordLoginResponse> => {
  // --- NEW FUNCTION ---
  const response = await axiosInstance.post<
    PasswordLoginResponse | ApiErrorResponse
  >("/auth/login/password", payload);
  // Using the specific AuthContextUserApiResponse type for checking
  const userData = (response.data as PasswordLoginResponse).data?.user;
  if (
    response.data.status === "success" &&
    "token" in response.data &&
    userData?.id &&
    userData.role &&
    userData.isKycSubmitted !== undefined &&
    userData.telegramIdLinked !== undefined
  ) {
    return response.data as PasswordLoginResponse;
  }
  throw new Error(
    (response.data as ApiErrorResponse).message ||
    "Password Login failed or returned invalid data."
  );
};

export const registerAdmin = async (
  payload: AdminRegistrationPayload
): Promise<{
  admin: { id: string; loginId: string; email: string };
  emailStatus: { sent: boolean; simulated: boolean };
}> => {
  const response = await axiosInstance.post<
    ApiResponse<{
      admin: { id: string; loginId: string; email: string };
      emailStatus: { sent: boolean; simulated: boolean };
    }> | ApiErrorResponse
  >("/auth/register/admin", payload);
  return handleApiResponse(response);
};

export const logoutUser = async (): Promise<void> => {
  try {
    await axiosInstance.post("/auth/logout");
  } catch (error) {
    console.error(
      "Error calling logout API (ignoring for local logout):",
      getErrorMessage(error)
    );
  } finally {
    clearAuthData();
  }
};

// --- User ---
export const getUserProfile = async (): Promise<UserProfile> => {
  // Expecting { status: 'success', data: { user: UserProfile } }
  const response = await axiosInstance.get<
    ApiResponse<{ user: UserProfile }> | ApiErrorResponse
  >("/users/me");
  const data = handleApiResponse(response);
  if (!data?.user) {
    throw new Error("User data not found in API response.");
  }
  // Ensure Discord fields have default values
  return {
    ...data.user,
    discordIdLinked: data.user.discordIdLinked ?? false,
    discord_user_id: data.user.discord_user_id ?? null,
    discord_username: data.user.discord_username ?? null,
    slackIdLinked: data.user.slackIdLinked ?? false,
    slack_user_id: data.user.slack_user_id ?? null,
    slack_username: data.user.slack_username ?? null,
  };
};


export const submitdob = async (payload: DOBPayload): Promise<void> => {
  const response = await axiosInstance.post<
    ApiResponse<void> | ApiErrorResponse
  >("/users/dob", payload);
  return handleApiResponse(response);
};
export const submitnamemail = async (payload: NameMailPayload): Promise<void> => {
  const response = await axiosInstance.post<
    ApiResponse<void> | ApiErrorResponse
  >("/users/namemail", payload);
  return handleApiResponse(response);
};
export const submitKyc = async (
  payload: UserKycPayload
): Promise<{ kycSubmittedAt: string }> => {
  const response = await axiosInstance.post<
    ApiResponse<{ kycSubmittedAt: string }> | ApiErrorResponse
  >("/users/kyc", payload);
  return handleApiResponse(response);
};
export const submitKycsub = async (
  payload: UserKycPayload
): Promise<{ kycSubmittedAt: string }> => {
  const response = await axiosInstance.post<
    ApiResponse<{ kycSubmittedAt: string }>
  >("/users/kyc/sub", payload);
  return handleApiResponse(response);
};
export const linkTelegramUsername = async (
  payload: UserTelegramPayload
): Promise<void> => {
  const response = await axiosInstance.post<ApiResponse>(
    "/users/me/telegram",
    payload
  );
  handleApiResponse(response);
};

export const unlinkTelegramAccount = async (): Promise<void> => {
  const response = await axiosInstance.post<ApiResponse>(
    "/users/me/telegram/unlink"
  );
  handleApiResponse(response);
};

export const syncSubscriptionAgreementStatus = async (
  subscriptionId: string
): Promise<{ signed: boolean; message: string }> => {
  const response = await axiosInstance.post<
    ApiResponse<{ subscription: unknown; signed: boolean }> | ApiErrorResponse
  >(`/users/subscriptions/${subscriptionId}/sync-agreement`);
  const data = handleApiResponse(response);
  return {
    signed: !!data?.signed,
    message:
      ("message" in response.data && typeof response.data.message === "string"
        ? response.data.message
        : "") || "",
  };
};

export interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
  is_member?: boolean;
  memberCount?: number;
}

export interface SlackWorkspaceInfo {
  id: string;
  name: string;
  url?: string;
}

export interface SlackConfiguredChannel {
  _id: string;
  name: string;
  slack_channel_id: string;
  slack_team_id?: string;
  is_active?: boolean;
  activeSubscriptions: number;
}

export interface SlackSyncReport {
  channelsProcessed: number;
  usersChecked: number;
  invitesSent: number;
  removalsSent: number;
  skippedNoSlackLinked: number;
  errors: string[];
}

export interface ChannelOnboardingSession {
  sessionId: string;
  platform: ChannelPlatform;
  generatedAt: string;
  deepLinkUrl: string;
  qrCodeUrl: string;
  ctaLabel: string;
  botDisplay: string;
  linkDisplay: string;
  requiredFields: string[];
  steps: string[];
  configHints: string[];
  frontendReference?: string;
}

export const getSlackWorkspaceChannels = async (): Promise<{
  channels: SlackChannel[];
  simulated: boolean;
}> => {
  const response = await axiosInstance.get<ApiResponse<{ channels: SlackChannel[]; simulated: boolean }>>(
    "/slack/channels"
  );
  const data = handleApiResponse(response);
  return {
    channels: data?.channels || [],
    simulated: !!data?.simulated,
  };
};

export const testSlackConnection = async (): Promise<{
  team: SlackWorkspaceInfo | null;
  botUserId: string | null;
  simulated: boolean;
}> => {
  const response = await axiosInstance.post<
    ApiResponse<{ team: SlackWorkspaceInfo | null; botUserId: string | null; simulated: boolean }>
  >("/slack/test-connection");
  const data = handleApiResponse(response);
  return {
    team: data?.team || null,
    botUserId: data?.botUserId || null,
    simulated: !!data?.simulated,
  };
};

export const getSlackConfiguredChannels = async (): Promise<{
  channels: SlackConfiguredChannel[];
  simulated: boolean;
}> => {
  const response = await axiosInstance.get<
    ApiResponse<{ channels: SlackConfiguredChannel[]; simulated: boolean }>
  >("/slack/configured-channels");
  const data = handleApiResponse(response);
  return {
    channels: data?.channels || [],
    simulated: !!data?.simulated,
  };
};

export const syncSlackAccess = async (channelId?: string): Promise<{
  report: SlackSyncReport;
  simulated: boolean;
}> => {
  const response = await axiosInstance.post<
    ApiResponse<{ report: SlackSyncReport; simulated: boolean }>
  >("/slack/sync-access", channelId ? { channelId } : {});
  const data = handleApiResponse(response);
  return {
    report: data?.report,
    simulated: !!data?.simulated,
  };
};

// --- Coupons (Admin / SuperAdmin) ---
export const getCoupons = async (): Promise<Coupon[]> => {
  const response = await axiosInstance.get<
    ApiResponse<{ coupons: Coupon[] }> | ApiErrorResponse
  >("/coupons");
  const data = handleApiResponse(response);
  return data?.coupons || [];
};

export const createCoupon = async (
  payload: CouponUpsertPayload
): Promise<Coupon> => {
  const response = await axiosInstance.post<
    ApiResponse<{ coupon: Coupon }> | ApiErrorResponse
  >("/coupons", payload);
  const data = handleApiResponse(response);
  if (!data?.coupon) {
    throw new Error("Coupon data not found in response.");
  }
  return data.coupon;
};

export const updateCoupon = async (
  couponId: string,
  payload: CouponUpsertPayload
): Promise<Coupon> => {
  const response = await axiosInstance.put<
    ApiResponse<{ coupon: Coupon }> | ApiErrorResponse
  >(`/coupons/${couponId}`, payload);
  const data = handleApiResponse(response);
  if (!data?.coupon) {
    throw new Error("Coupon data not found in response.");
  }
  return data.coupon;
};

export const deactivateCoupon = async (couponId: string): Promise<Coupon> => {
  const response = await axiosInstance.post<
    ApiResponse<{ coupon: Coupon }> | ApiErrorResponse
  >(`/coupons/${couponId}/deactivate`);
  const data = handleApiResponse(response);
  if (!data?.coupon) {
    throw new Error("Coupon data not found in response.");
  }
  return data.coupon;
};

export const previewCoupon = async (payload: {
  couponCode?: string;
  plan_id?: string;
  combo_id?: string;
  isCombo?: boolean;
  condition?: "first_time" | "renewal" | "upgrade";
}): Promise<CouponPreviewResponse> => {
  const response = await axiosInstance.post<
    ApiResponse<{ pricing: CouponPreviewResponse }> | ApiErrorResponse
  >("/coupons/preview", payload);
  const data = handleApiResponse(response);
  if (!data?.pricing) {
    throw new Error("Coupon pricing not found in response.");
  }
  return data.pricing;
};

// Legacy wrappers kept to avoid breaking old imports during migration.
export const getUniversalCoupon = async (): Promise<UniversalCoupon | null> => {
  const coupons = await getCoupons();
  return coupons[0] || null;
};

export const upsertUniversalCoupon = async (
  payload: UniversalCouponUpsertPayload
): Promise<UniversalCoupon> => {
  const existing = await getUniversalCoupon();
  if (existing?._id) {
    return updateCoupon(existing._id, payload);
  }
  return createCoupon(payload);
};

export const deactivateUniversalCoupon = async (): Promise<UniversalCoupon> => {
  const existing = await getUniversalCoupon();
  if (!existing?._id) {
    throw new Error("No active coupon found.");
  }
  return deactivateCoupon(existing._id);
};

// --- Discord Admin API ---
export interface DiscordGuild {
  id: string;
  name: string;
  memberCount?: number;
  icon?: string | null;
}

export interface DiscordRole {
  id: string;
  name: string;
  color?: number;
  position?: number;
  channelId?: string;
  channelName?: string;
}

export interface DiscordSyncReport {
  rolesAssigned: number;
  rolesRemoved: number;
  errors: string[];
  details?: string[];
}

export const getDiscordBotGuilds = async (): Promise<{
  guilds: DiscordGuild[];
  simulated: boolean;
}> => {
  const response = await axiosInstance.get<any>("/discord/bot-guilds");

  if (response.data?.success) {
    return {
      guilds: response.data.data || [],
      simulated: response.data.simulated || false,
    };
  }
  throw new Error("Failed to fetch bot guilds");
};

export const checkDiscordGuild = async (guildId: string): Promise<{
  inGuild: boolean;
  data?: DiscordGuild;
}> => {
  const response = await axiosInstance.get<any>(`/discord/guild/${guildId}/check`);

  if (response.data?.success) {
    return {
      inGuild: response.data.inGuild,
      data: response.data.data,
    };
  }
  throw new Error("Failed to check guild");
};

export const getDiscordGuildRoles = async (guildId: string): Promise<{
  roles: DiscordRole[];
  simulated: boolean;
}> => {
  const response = await axiosInstance.get<any>(`/discord/guild/${guildId}/roles`);

  if (response.data?.success) {
    return {
      roles: response.data.data || [],
      simulated: response.data.simulated || false,
    };
  }
  throw new Error("Failed to fetch guild roles");
};

export const syncDiscordRoles = async (guildId: string): Promise<{
  report: DiscordSyncReport;
  simulated: boolean;
}> => {
  const response = await axiosInstance.post<any>("/discord/sync-roles", { guildId });

  if (response.data?.success) {
    return {
      report: response.data.report,
      simulated: response.data.simulated || false,
    };
  }
  throw new Error(response.data?.error || "Failed to sync roles");
};

// Create Discord invite link for a channel
export const createDiscordInviteLink = async (
  guildId: string,
  channelId: string
): Promise<{
  inviteUrl: string;
  simulated: boolean;
}> => {
  const response = await axiosInstance.post<any>("/discord/invite", {
    guildId,
    channelId,
  });

  if (response.data?.success) {
    return {
      inviteUrl: response.data.inviteUrl,
      simulated: response.data.simulated || false,
    };
  }
  throw new Error(response.data?.error || "Failed to create invite link");
};

// Get Discord channels (text channels) in a guild
export const getDiscordGuildChannels = async (guildId: string): Promise<{
  channels: Array<{ id: string; name: string; type: number }>;
  simulated: boolean;
}> => {
  const response = await axiosInstance.get<any>(`/discord/guild/${guildId}/channels`);

  if (response.data?.success) {
    return {
      channels: response.data.data || [],
      simulated: response.data.simulated || false,
    };
  }
  throw new Error("Failed to fetch guild channels");
};

// --- Subscriptions (User) ---
export const getUserSubscriptions = async (): Promise<UserSubscription[]> => {
  // Expecting { status: 'success', results?: number, data: { subscriptions: UserSubscription[] } }
  const response = await axiosInstance.get<
    ApiResponse<{ subscriptions: UserSubscription[] }> | ApiErrorResponse
  >("/subscriptions/my-groups");
  const data = handleApiResponse(response);
  return data?.subscriptions || []; // Return empty array if data or subscriptions is missing
};

// --- NewSub Flow ---
// Using the shared InitiateTransactionOrderResponse type
export const initiateSubscribe = async (payload: {
  plan_id: string;
  referral_code?: string;
  couponCode?: string;
  referralCode: string;
  isCombo: boolean;
}): Promise<InitiateTransactionOrderResponse> => {
  const response = await axiosInstance.post<
    ApiResponse<InitiateTransactionOrderResponse> | ApiErrorResponse
  >(`/transactions/order`, payload);
  return handleApiResponse(response);
};

export const devBypassAgreement = async (payload: {
  plan_id: string;
  couponCode?: string;
  referralCode: string;
  isCombo: boolean;
}): Promise<{
  bypassed: boolean;
  transactionId: string;
  subscriptions: Array<{
    _id: string;
    status: string;
    digio_document_id?: string | null;
    digio_signing_url?: string | null;
    digio_last_error?: string | null;
  }>;
  digioSigningUrl?: string | null;
}> => {
  const response = await axiosInstance.post<
    ApiResponse<{
      bypassed: boolean;
      transactionId: string;
      subscriptions: Array<{
        _id: string;
        status: string;
        digio_document_id?: string | null;
        digio_signing_url?: string | null;
        digio_last_error?: string | null;
      }>;
      digioSigningUrl?: string | null;
    }> | ApiErrorResponse
  >(`/transactions/dev-bypass-agreement`, payload);
  return handleApiResponse(response);
};

// --- Upgrade Flow ---
// Using the shared InitiateTransactionOrderResponse type
export const initiateUpgrade = async (
  subscriptionId: string,
  payload: SubscriptionUpgradePayload
): Promise<InitiateTransactionOrderResponse> => {
  // --- MODIFIED RETURN TYPE ---
  const response = await axiosInstance.post<
    ApiResponse<InitiateTransactionOrderResponse> | ApiErrorResponse
  >(`/subscriptions/upgrade/${subscriptionId}`, payload);
  return handleApiResponse(response);
};

export const verifyTransactionPayment = async (payload: {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}): Promise<{ alreadyProcessed: boolean; transactionId: string; subscriptions: string[] }> => {
  const response = await axiosInstance.post<
    ApiResponse<{
      alreadyProcessed: boolean;
      transactionId: string;
      subscriptions: string[];
      transactionStatus?: string;
    }> | ApiErrorResponse
  >(`/transactions/verify-payment`, payload);

  const data = handleApiResponse(response);
  return {
    alreadyProcessed: !!data?.alreadyProcessed,
    transactionId: data?.transactionId || "",
    subscriptions: data?.subscriptions || [],
  };
};

// --- Transactions (User) ---
export const getTransactionInvoice = async (
  transactionId: string
): Promise<{ invoiceUrl: string }> => {
  // --- MODIFIED RETURN TYPE - always returns object or throws ---
  try {
    const response = await axiosInstance.get<
      ApiResponse<{ invoiceUrl: string }> | ApiErrorResponse
    >(`/transactions/${transactionId}/invoice`);
    const data = handleApiResponse(response);

    if (data?.invoiceUrl) {
      return data;
    } else {
      // API returned success but no invoiceUrl, treat as not found/unavailable operationally
      // throw new Error('Invoice URL not found in successful response.'); // Or handle as specific AppError like backend
      const errorMsg =
        (response.data as ApiErrorResponse)?.message ||
        "Invoice URL not found in response.";
      throw new Error(errorMsg); // Throw the message from backend error if available
    }
  } catch (error: unknown) {
    const message = getErrorMessage(error);

    if (
      message.toLowerCase().includes("not found") ||
      message.toLowerCase().includes("not available") ||
      message.toLowerCase().includes("no invoice available")
    ) {
      throw new Error("Invoice not available or ready.");
    }

    console.error("Error fetching invoice:", message);
    throw new Error(message || "Failed to fetch invoice.");
  }
};

// --- Public Channel & Plan Access ---
export const getPublicChannelByReferralCode = async (
  referralCode: string
): Promise<PopulatedChannel> => {
  // Expecting { status: 'success', data: { channel: PopulatedChannel } }
  const response = await axiosInstance.get<
    ApiResponse<{ channel: PopulatedChannel }> | ApiErrorResponse
  >(`/channels/public/${referralCode}`);
  const data = handleApiResponse(response);
  const channelData = data?.channel;
  if (!channelData) {
    throw new Error("Channel not found or is inactive.");
  }
  return channelData;
};

// --- Channels (Used by Admin/SA - Backend filters results) ---
export const getChannelDetails = async (
  channelId: string
): Promise<PopulatedChannel> => {
  // Expecting { status: 'success', data: { channel: PopulatedChannel } }
  const response = await axiosInstance.get<
    ApiResponse<{ channel: PopulatedChannel }> | ApiErrorResponse
  >(`/channels/${channelId}`);
  const data = handleApiResponse(response);
  const channelData = data?.channel;
  if (!channelData) {
    throw new Error("Channel data not found in API response.");
  }
  return channelData;
};

// --- ADMIN / SUPER ADMIN API Calls ---

export interface GovernancePermission {
  _id: string;
  resource: string;
  action: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GovernanceRole {
  _id: string;
  name: string;
  description?: string;
  permissions: GovernancePermission[];
  createdAt?: string;
  updatedAt?: string;
}

export const getAllGovernancePermissions = async (): Promise<
  GovernancePermission[]
> => {
  const response = await axiosInstance.get<
    ApiResponse<{ permissions: GovernancePermission[] }> | ApiErrorResponse
  >("/permissions");
  const data = handleApiResponse(response);
  return data?.permissions || [];
};

export const getAllGovernanceRoles = async (): Promise<GovernanceRole[]> => {
  const response = await axiosInstance.get<
    ApiResponse<{ roles: GovernanceRole[] }> | ApiErrorResponse
  >("/roles");
  const data = handleApiResponse(response);
  return data?.roles || [];
};

export const updateGovernanceRolePermissions = async (
  roleId: string,
  permissionIds: string[]
): Promise<GovernanceRole> => {
  const response = await axiosInstance.put<
    ApiResponse<{ role: GovernanceRole }> | ApiErrorResponse
  >(`/roles/${roleId}/permissions`, { permissionIds });
  const data = handleApiResponse(response);
  if (!data?.role) {
    throw new Error("Role not found in API response.");
  }
  return data.role;
};

// --- Dashboard ---
export const getDashboardSummary =
  async (): Promise<DashboardSummaryResponse> => {
    // Expecting { status: 'success', data: DashboardSummaryResponse }
    const response = await axiosInstance.get<
      ApiResponse<DashboardSummaryResponse> | ApiErrorResponse
    >("/analytics/dashboard-summary");
    return handleApiResponse(response);
  };

export async function getAdminDashboardSummary(
  startDate: string,
  endDate: string,
  aggregation: "mon" | "yer" | "day" | "none",
  year: number,
  month: number,
  skipCache = false,
): Promise<AdminDashboardSummaryResponse> {
  const cacheKey = makeCacheKey("admin_dash", { startDate, endDate, aggregation, year, month });
  if (!skipCache) {
    const cached = getCache<AdminDashboardSummaryResponse>(cacheKey);
    if (cached) return cached;
  }
  const response = await axiosInstance.post("/analytics/admin/dashboard", {
    start_date: startDate,
    end_date: endDate,
    aggregation,
    year,
    month,
  });
  setCache(cacheKey, response.data);
  return response.data;
}

export async function getTransactionsByPlatform(
  platform: string,
  startDate: string,
  endDate: string
): Promise<{ transactions: TransactionAdminResponse[] }> {
  const response = await axiosInstance.get("/analytics/admin/transactions-by-platform", {
    params: { platform, start_date: startDate, end_date: endDate }
  });
  return response.data.data;
}
export async function getChannelDashboardSummary(
  channelId: string,
  startDate: string | Date,
  endDate: string | Date,
  aggregation: "mon" | "yer" | "day" | "none",
  year: number,
  month: number,
  planId?: string,
  referralCode?: string,
  skipCache = false,
): Promise<ChannelDashboardSummaryResponse> {
  const cacheKey = makeCacheKey("ch_dash", {
    channelId,
    startDate: String(startDate),
    endDate: String(endDate),
    aggregation,
    year,
    month,
    planId,
    referralCode,
  });
  if (!skipCache) {
    const cached = getCache<ChannelDashboardSummaryResponse>(cacheKey);
    if (cached) return cached;
  }
  const response = await axiosInstance.post(
    "/analytics/admin/channel/dashboard",
    {
      channel_id: channelId,
      start_date: startDate,
      aggregation,
      year,
      month,
      end_date: endDate,
      ...(planId && { plan_id: planId }),
      ...(referralCode && { referralCode: referralCode }),
    }
  );
  setCache(cacheKey, response.data);
  return response.data;
}
// New function (Combo Dashboard)
export async function getComboDashboardSummary(
  comboId: string,
  startDate: string | Date,
  endDate: string | Date,
  aggregation: "mon" | "yer" | "day" | "none",
  year: number,
  month: number,
  referralCode?: string,
  skipCache = false,
): Promise<ComboDashboardSummaryResponse> {
  const cacheKey = makeCacheKey("co_dash", {
    comboId,
    startDate: String(startDate),
    endDate: String(endDate),
    aggregation,
    year,
    month,
    referralCode,
  });
  if (!skipCache) {
    const cached = getCache<ComboDashboardSummaryResponse>(cacheKey);
    if (cached) return cached;
  }
  const response = await axiosInstance.post(
    "/analytics/admin/combo/dashboard",
    {
      combo_id: comboId,
      start_date: startDate,
      aggregation,
      year,
      month,
      end_date: endDate,
      ...(referralCode && { referralCode: referralCode }),
    }
  );
  setCache(cacheKey, response.data);
  return response.data;
}
// --- Users ---
// Fetches users. Backend controller logic filters based on role (SA sees all, Admin sees theirs)
export const getUsersList = async (
  filters: { phone?: string; role?: string; page?: number; limit?: number } = {}
): Promise<{ users: UserProfile[]; total: number }> => {
  // --- MODIFIED RETURN TYPE ---
  // Expecting { status: 'success', results: number, total: number, data: { users: UserProfile[] } }
  const response = await axiosInstance.get<
    ListResponseWithTotal<{ users: UserProfile }> | ApiErrorResponse
  >("/users", { params: filters });
  // Use the helper to handle list responses with total
  return handleListResponseWithTotal(response, "users");
};

// SuperAdmin specific: Create Admin User
export const createRoleUser = async (payload: {
  loginId: string;
  password?: string;
  name: string;
  roleName: string;
  belongs_to?: string;
}): Promise<{ user: UserProfile; message: string }> => {
  // Backend returns UserProfile in 'user' field
  const response = await axiosInstance.post<
    ApiResponse<{ user: UserProfile; message: string }> | ApiErrorResponse
  >("/users/create-role-user", payload);
  return handleApiResponse(response); // This expects data.data
};
// SuperAdmin specific: Update User
export const updateRoleUser = async (payload: {
  loginId: string;
  password: string;
}): Promise<{ message: string }> => {
  // Backend returns UserProfile in 'user' field
  const response = await axiosInstance.post<
    ApiResponse<{ message: string }> | ApiErrorResponse
  >("/users/update-role-user", payload);
  return handleApiResponse(response); // This expects data.data
};

// --- Channels ---
// Fetches channels. Backend controller filters based on role (SA sees all + owner, Admin sees owned)
// Currently backend getAllChannels for SA returns channels with populated owner but ASSOCIATED_PLAN_IDS as strings.
// The frontend SA Channels page will now need populated plans, so this endpoint needs to return PopulatedChannel[].
export const getAdminChannels = async (
  filters: {
    name?: string;
    is_active?: boolean;
    page?: number;
    limit?: number;
  } = {}
): Promise<PopulatedChannel[]> => {
  // --- MODIFIED RETURN TYPE ---
  // Expecting { status: 'success', results: number, data: { channels: PopulatedChannel[] } } (for SA)
  const response = await axiosInstance.get<
    ApiResponse<{ channels: PopulatedChannel[] }> | ApiErrorResponse
  >("/channels", { params: filters });
  const data = handleApiResponse(response);
  return data?.channels || [];
};

export const createChannelOnboardingSession = async (
  platform: ChannelPlatform
): Promise<ChannelOnboardingSession> => {
  const response = await axiosInstance.post<
    ApiResponse<{ onboarding: ChannelOnboardingSession }> | ApiErrorResponse
  >("/channels/onboarding/session", { platform });
  const data = handleApiResponse(response);
  if (!data?.onboarding) {
    throw new Error("Onboarding session data not found in response.");
  }
  return data.onboarding;
};

// ... createAdminChannel ...
export const createAdminChannel = async (
  payload: ChannelCreatePayload
): Promise<Channel> => {
  // Expecting { status: 'success', data: { channel: Channel } }
  const response = await axiosInstance.post<
    ApiResponse<{ channel: Channel }> | ApiErrorResponse
  >("/channels", payload);
  const data = handleApiResponse(response);
  if (!data?.channel) throw new Error("Channel data not found in response.");
  return data.channel;
};

// ... updateAdminChannel ...
export const updateAdminChannel = async (
  channelId: string,
  payload: ChannelUpdatePayload
): Promise<Channel> => {
  // Expecting { status: 'success', data: { channel: Channel } }
  const response = await axiosInstance.put<
    ApiResponse<{ channel: Channel }> | ApiErrorResponse
  >(`/channels/${channelId}`, payload);
  const data = handleApiResponse(response);
  if (!data?.channel) throw new Error("Channel data not found in response.");
  return data.channel;
};

// ... deleteAdminChannel ...
export const deleteAdminChannel = async (channelId: string): Promise<void> => {
  try {
    // Expecting 204 No Content or 200 with success status and no data
    const response = await axiosInstance.delete<ApiResponse | ApiErrorResponse>(
      `/channels/${channelId}`
    );
    console.log(response);
    handleApiResponse(response);

    return; // Return void on success
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error));
  }
};
// --- Referral Codes ---
// Add a referral code to a channel
export const addReferralCode = async (channelId: string): Promise<string> => {
  // Backend returns { status: "success" }
  const response = await axiosInstance.post<
    ApiResponse<{ code: string }> | ApiErrorResponse
  >(`/channels/${channelId}/referral-codes`);
  const data = handleApiResponse(response);
  if (!data?.code) throw new Error("Failed to get referral code from response.");
  return data.code;
};

// Delete a referral code from a channel
export const deleteReferralCode = async (
  channelId: string,
  code: string
): Promise<void> => {
  // Backend returns { status: "success" }
  const response = await axiosInstance.delete<ApiResponse | ApiErrorResponse>(
    `/channels/${channelId}/referral-codes/${code}`
  );
  handleApiResponse(response);
  return; // void
};

// ... activateAdminChannel ...
export const activateAdminChannel = async (
  channelId: string
): Promise<Channel> => {
  // Expecting { status: 'success', message: '...', data: { channel: Channel } }
  const response = await axiosInstance.put<
    ApiResponse<{ channel: Channel }> | ApiErrorResponse
  >(`/channels/${channelId}/activate`);
  const data = handleApiResponse(response);
  if (!data?.channel) throw new Error("Channel data not found in response.");
  return data.channel;
};

// ... deactivateAdminChannel ...
export const deactivateAdminChannel = async (
  channelId: string
): Promise<Channel> => {
  // Expecting { status: 'success', message: '...', data: { channel: Channel } }
  const response = await axiosInstance.put<
    ApiResponse<{ channel: Channel }> | ApiErrorResponse
  >(`/channels/${channelId}/deactivate`);
  const data = handleApiResponse(response);
  if (!data?.channel) throw new Error("Channel data not found in response.");
  return data.channel;
};

// --- Plans ---
// Fetches plans. Backend controller filters based on role (SA sees all + channel/owner, Admin sees plans for owned channels)
// SA view needs channel/owner populated.
export const getAdminPlans = async (
  filters: {
    name?: string;
    channel?: string;
    is_active?: boolean;
    page?: number;
    limit?: number;
  } = {}
): Promise<Plan[]> => {
  // --- MODIFIED RETURN TYPE ---
  // Expecting { status: 'success', results: number, data: { plans: Plan[] } } (with channel_id populated for SA)
  const response = await axiosInstance.get<
    ApiResponse<{ plans: Plan[] }> | ApiErrorResponse
  >("/plans", { params: filters });
  const data = handleApiResponse(response);
  return data?.plans || [];
};

// ... getPlansForChannel ...
export const getPlansForChannel = async (
  channelId: string
): Promise<Plan[]> => {
  // Expecting { status: 'success', results: number, data: { plans: Plan[] } } (where plans have channel_id as string/basic object)
  const response = await axiosInstance.get<
    ApiResponse<{ plans: Plan[] }> | ApiErrorResponse
  >(`/plans/channel/${channelId}`);
  const data = handleApiResponse(response);
  return data?.plans || [];
};

// Delete Plan (Hard Delete)
// Just remove the plan document AND the association.
export const deleteAdminPlan = async (planId: string): Promise<void> => {
  // --- RENAMED, MODIFIED RETURN TYPE ---
  try {
    // Expecting success status or 200/204
    const response = await axiosInstance.delete<ApiResponse | ApiErrorResponse>(
      `/plans/${planId}`
    );
    handleApiResponse(response);
    return; // Return void on success
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error));
  }
};

// ... getPlanDetails ...
export const getPlanDetails = async (planId: string): Promise<Plan> => {
  // Expecting { status: 'success', data: { plan: Plan } } (with channel_id populated for SA)
  const response = await axiosInstance.get<
    ApiResponse<{ plan: Plan }> | ApiErrorResponse
  >(`/plans/${planId}`);
  const data = handleApiResponse(response);
  const planData = data?.plan;
  if (!planData) {
    throw new Error("Plan data not found in API response.");
  }
  return planData;
};

// ... createAdminPlan ...
export const createAdminPlan = async (
  payload: PlanCreatePayload
): Promise<Plan> => {
  // Expecting { status: 'success', data: { plan: Plan } }
  const response = await axiosInstance.post<
    ApiResponse<{ plan: Plan }> | ApiErrorResponse
  >("/plans", payload);
  const data = handleApiResponse(response);
  if (!data?.plan) throw new Error("Plan data not found in response.");
  return data.plan;
};

// ... updateAdminPlan ...
export const updateAdminPlan = async (
  planId: string,
  payload: PlanUpdatePayload
): Promise<Plan> => {
  // Expecting { status: 'success', data: { plan: Plan } }
  const response = await axiosInstance.put<
    ApiResponse<{ plan: Plan }> | ApiErrorResponse
  >(`/plans/${planId}`, payload);
  const data = handleApiResponse(response);
  if (!data?.plan) throw new Error("Plan data not found in response.");
  return data.plan;
};

// ... activateAdminPlan ...
export const activateAdminPlan = async (planId: string): Promise<Plan> => {
  // Expecting { status: 'success', message: '...', data: { plan: Plan } }
  const response = await axiosInstance.put<
    ApiResponse<{ plan: Plan }> | ApiErrorResponse
  >(`/plans/${planId}/activate`);
  const data = handleApiResponse(response);
  if (!data?.plan) throw new Error("Plan data not found in response.");
  return data.plan;
};

// ... deactivateAdminPlan ...
export const deactivateAdminPlan = async (planId: string): Promise<Plan> => {
  // Expecting { status: 'success', message: '...', data: { plan: Plan } }
  const response = await axiosInstance.put<
    ApiResponse<{ plan: Plan }> | ApiErrorResponse
  >(`/plans/${planId}/deactivate`);
  const data = handleApiResponse(response);
  if (!data?.plan) throw new Error("Plan data not found in response.");
  return data.plan;
};

// --- Combo Plans ---

export const getAdminComboSubscriptions = async (
  combo_id: string,
  filters: {
    userPhone?: string;
    status?: string;
    page?: number;
    limit?: number;
  } = {}
): Promise<{ subscriptions: SubscriptionAdminResponse[]; total: number }> => {
  // --- MODIFIED RETURN TYPE ---
  const params = { ...filters, combo_id: combo_id };
  // Expecting { status: 'success', results: number, total: number, data: { subscriptions: SubscriptionAdminResponse[] } }
  const response = await axiosInstance.get<
    | ListResponseWithTotal<{ subscriptions: SubscriptionAdminResponse }>
    | ApiErrorResponse
  >("/subscriptions", { params });
  // Helper to handle list responses with total
  return handleListResponseWithTotal(response, "subscriptions");
};

export const getPublicComboByReferralCode = async (
  referralCode: string
): Promise<PopulatedCombo> => {
  // Expecting { status: 'success', data: { combo: PopulatedCombo } }
  const response = await axiosInstance.get<
    ApiResponse<{ combo: PopulatedCombo }> | ApiErrorResponse
  >(`/combos/public/${referralCode}`);

  const data = handleApiResponse(response);
  const comboData = data?.combo;

  if (!comboData) {
    throw new Error("Combo not found or is inactive.");
  }

  return comboData;
};

export const getComboDetails = async (
  comboId: string
): Promise<PopulatedCombo> => {
  const response = await axiosInstance.get<
    ApiResponse<{ combo: PopulatedCombo }> | ApiErrorResponse
  >(`/combos/${comboId}`);
  const data = handleApiResponse(response);
  const combo = data?.combo;
  if (!combo) {
    throw new Error("Channel data not found in API response.");
  }
  return combo;
};

// --- Types (adjust to your model shape) ---

// --- Get all combos (Admin / SA) ---
export const getAdminCombos = async (
  filters: {
    name?: string;
    is_active?: boolean;
    page?: number;
    limit?: number;
  } = {}
): Promise<PopulatedCombo[]> => {
  // Backend returns { status, results, data: { combos } }
  const response = await axiosInstance.get<
    ApiResponse<{ combos: PopulatedCombo[] }> | ApiErrorResponse
  >("/combos", { params: filters });
  const data = handleApiResponse(response);
  return data?.combos || [];
};

// --- Create Combo ---
export const createAdminCombo = async (
  payload: Partial<Combo>
): Promise<Combo> => {
  // Backend returns { status, data: { combo } }
  const response = await axiosInstance.post<
    ApiResponse<{ combo: Combo }> | ApiErrorResponse
  >("/combos", payload);
  const data = handleApiResponse(response);
  if (!data?.combo) throw new Error("Combo data not found in response.");
  return data.combo;
};

// --- Update Combo ---
export const updateAdminCombo = async (
  comboId: string,
  payload: Partial<Combo>
): Promise<Combo> => {
  const response = await axiosInstance.put<
    ApiResponse<{ combo: Combo }> | ApiErrorResponse
  >(`/combos/${comboId}`, payload);
  const data = handleApiResponse(response);
  if (!data?.combo) throw new Error("Combo data not found in response.");
  return data.combo;
};

// --- Delete Combo ---
export const deleteAdminCombo = async (comboId: string): Promise<void> => {
  try {
    const response = await axiosInstance.delete<
      ApiResponse | ApiErrorResponse
    >(`/combos/${comboId}`);
    handleApiResponse(response);
    return;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error));
  }
};

// --- Referral Codes ---
// Add a referral code to a combo
export const addComboReferralCode = async (comboId: string): Promise<string> => {
  // Backend returns { status: "success", data: { code: string } }
  const response = await axiosInstance.post<
    ApiResponse<{ code: string }> | ApiErrorResponse
  >(`/combos/${comboId}/referral-codes`);
  const data = handleApiResponse(response);
  if (!data?.code) throw new Error("Failed to get referral code from response.");
  return data.code;
};

// Delete a referral code from a combo
export const deleteComboReferralCode = async (
  comboId: string,
  code: string
): Promise<void> => {
  const response = await axiosInstance.delete<ApiResponse | ApiErrorResponse>(
    `/combos/${comboId}/referral-codes/${code}`
  );
  handleApiResponse(response);
  return;
};


// Subscriptions (Admin/SA - backend handles filtering)

// Uses SubscriptionAdminResponse type which expects user_id populated with basic info.
export const getAdminChannelSubscriptions = async (
  channelId: string,
  filters: {
    userPhone?: string;
    status?: string;
    page?: number;
    limit?: number;
  } = {}
): Promise<{ subscriptions: SubscriptionAdminResponse[]; total: number }> => {
  // --- MODIFIED RETURN TYPE ---
  const params = { ...filters, channel_id: channelId };
  // Expecting { status: 'success', results: number, total: number, data: { subscriptions: SubscriptionAdminResponse[] } }
  const response = await axiosInstance.get<
    | ListResponseWithTotal<{ subscriptions: SubscriptionAdminResponse }>
    | ApiErrorResponse
  >("/subscriptions", { params });
  // Helper to handle list responses with total
  return handleListResponseWithTotal(response, "subscriptions");
};
export const getAdminChannelLeads = async (
  filters: {
    channel_id?: string;
    page?: number;
    limit?: number;
    start_date?: string;
    end_date?: string;
  } = {}
): Promise<{ transactions: TransactionAdminResponse[]; total: number }> => {
  const response = await axiosInstance.get<
    | ListResponseWithTotal<{ transactions: TransactionAdminResponse }>
    | ApiErrorResponse
  >("/transactions/incomplete", { params: filters });

  return handleListResponseWithTotal(response, "transactions");
};
// Extend Subscription (Used by Support/Admin/SA - backend checks permissions)
export const extendSubscription = async (
  subId: string,
  payload: SubscriptionExtendPayload
): Promise<SubscriptionAdminResponse> => {
  // Expecting { status: 'success', message: '...', data: { subscription: SubscriptionAdminResponse } }
  const response = await axiosInstance.put<
    ApiResponse<{ subscription: SubscriptionAdminResponse }> | ApiErrorResponse
  >(`/subscriptions/extend/${subId}`, payload);
  const data = handleApiResponse(response);
  if (!data?.subscription)
    throw new Error("Subscription data not found in response.");
  return data.subscription;
};

// Revoke Subscription (Used by Admin/SA - backend checks permissions)
export const revokeSubscription = async (subId: string): Promise<void> => {
  try {
    // Expecting success status or 200/204
    const response = await axiosInstance.put<ApiResponse | ApiErrorResponse>(
      `/subscriptions/revoke/${subId}`
    );
    handleApiResponse(response);
    return;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error));
  }
};

// --- Manual Add User (Admin/SA) ---
export const manualAddUserToChannel = async (
  payload: {
    phone: string;
    channel_id: string;
    amount: number;
    plan_id?: string;
    should_kyc?: boolean;
    skip_agreement_signing?: boolean;
  }
): Promise<{
  whatsappSent: boolean;
}> => {
  const response = await axiosInstance.post<
    ApiResponse<{
      whatsappSent: boolean;
    }> | ApiErrorResponse
  >("/users/manual-add-user", payload);
  return handleApiResponse(response);
};

// Reminder Templates (Read-only for Form Selection)
export const getReminderTemplates = async (): Promise<ReminderTemplate[]> => {
  // Expecting { status: 'success', results?: number, data: { templates: ReminderTemplate[] } }
  const response = await axiosInstance.get<
    ApiResponse<{ templates: ReminderTemplate[] }> | ApiErrorResponse
  >("/reminders/templates");
  const data = handleApiResponse(response);
  return data?.templates || [];
};

// --- Client Config (Admin / SuperAdmin) ---
export const getMyClientConfig = async (
  ownerId?: string
): Promise<ClientConfigRecord> => {
  const response = await axiosInstance.get<
    ApiResponse<{ clientConfig: ClientConfigRecord }> | ApiErrorResponse
  >("/client-config/me", {
    params: ownerId ? { ownerId } : undefined,
  });
  const data = handleApiResponse(response);
  if (!data?.clientConfig) {
    throw new Error("Client config not found in response.");
  }
  return data.clientConfig;
};

export const updateMyClientConfig = async (
  config: Partial<ClientConfigData>,
  ownerId?: string
): Promise<ClientConfigRecord> => {
  const response = await axiosInstance.patch<
    ApiResponse<{ clientConfig: ClientConfigRecord }> | ApiErrorResponse
  >(
    "/client-config/me",
    { config },
    { params: ownerId ? { ownerId } : undefined }
  );
  const data = handleApiResponse(response);
  if (!data?.clientConfig) {
    throw new Error("Client config not found in response.");
  }
  return data.clientConfig;
};

export const validateClientConfigSection = async (
  section: "platform" | "payment" | "kyc" | "app",
  ownerId?: string
): Promise<{ section: string; validation: ClientConfigValidationItem }> => {
  const response = await axiosInstance.post<
    ApiResponse<{ section: string; validation: ClientConfigValidationItem }> |
    ApiErrorResponse
  >(
    "/client-config/validate",
    { section },
    { params: ownerId ? { ownerId } : undefined }
  );
  return handleApiResponse(response);
};

export const rotateClientConfigSecret = async (
  path: string,
  value: string,
  ownerId?: string
): Promise<ClientConfigRecord> => {
  const response = await axiosInstance.post<
    ApiResponse<{ clientConfig: ClientConfigRecord }> | ApiErrorResponse
  >(
    "/client-config/rotate-secret",
    { path, value },
    { params: ownerId ? { ownerId } : undefined }
  );
  const data = handleApiResponse(response);
  if (!data?.clientConfig) {
    throw new Error("Client config not found in response.");
  }
  return data.clientConfig;
};

export const syncTelegramWebhookFromClientConfig = async (
  webhookBaseUrl?: string,
  ownerId?: string
): Promise<TelegramWebhookSyncInfo> => {
  const response = await axiosInstance.post<
    ApiResponse<{ telegram: TelegramWebhookSyncInfo }> | ApiErrorResponse
  >(
    "/client-config/telegram/sync-webhook",
    webhookBaseUrl ? { webhookBaseUrl } : {},
    { params: ownerId ? { ownerId } : undefined }
  );
  const data = handleApiResponse(response);
  if (!data?.telegram) {
    throw new Error("Telegram webhook sync response missing telegram payload.");
  }
  return data.telegram;
};

export const getTelegramWebhookInfo = async (ownerId?: string): Promise<{ connected: boolean; url: string }> => {
  const response = await axiosInstance.get<
    ApiResponse<{ connected: boolean; url: string }> | ApiErrorResponse
  >(
    "/client-config/telegram/webhook-info",
    { params: ownerId ? { ownerId } : undefined }
  );
  const data = handleApiResponse(response);
  return { connected: data?.connected ?? false, url: data?.url ?? "" };
};

export const uploadBrandingLogo = async (file: File): Promise<{ logoUrl: string }> => {
  const formData = new FormData();
  formData.append("logo", file);
  const response = await axiosInstance.post<ApiResponse<{ logoUrl: string }> | ApiErrorResponse>(
    "/client-config/upload-logo",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return handleApiResponse(response);
};
